import React from "react";
import { Leaf, MapPin, Sparkles, Home, Heart, ArrowRight, Users } from "lucide-react";
import PageHero from "../components/blocks/PageHero";
import PremiumButton from "../components/ui/PremiumButton";
import useSEO from "../hooks/useSEO";

// Section divider component
const SectionDivider = () => (
  <div className="w-full max-w-md mx-auto py-8">
    <div className="h-px bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent" />
  </div>
);

// Section heading component
const SectionHeading = ({ children, subtitle, centered = true }) => (
  <div className={`mb-10 ${centered ? 'text-center' : ''}`}>
    <h2 className="text-2xl md:text-4xl font-light text-white tracking-tight mb-3">{children}</h2>
    {subtitle && <p className="text-white/60 text-sm md:text-base max-w-2xl mx-auto">{subtitle}</p>}
  </div>
);

// Feature card for "What Makes Us Unique" section
const FeatureItem = ({ children }) => (
  <div className="flex items-start gap-3">
    <div className="w-2 h-2 rounded-full bg-[#C9A227] mt-2 flex-shrink-0" />
    <span className="text-white/70 text-sm md:text-base leading-relaxed">{children}</span>
  </div>
);

const About = () => {
  useSEO('/about');
  
  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="About Us"
        subtitle="A Place to Pause, Breathe, and Unwind"
        image="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=2400&q=80"
      />

      {/* Introduction Section */}
      <section className="py-20 md:py-28" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(201,162,39,0.08), transparent 45%), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.04), transparent 42%)" }}>
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 mb-8">
            <Leaf className="w-4 h-4 text-[#C9A227]" />
            <span className="text-xs uppercase tracking-widest text-[#C9A227]">Glow LAWNS Resort & Homestay</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-light text-white mb-8 leading-tight">
            A Place to Pause, Breathe,<br />
            <span className="text-[#C9A227]">and Unwind</span>
          </h2>
          
          <div className="space-y-6 text-white/70 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            <p>
              Glow LAWNS Resort & Homestay is a peaceful retreat in North Kerala where nature, comfort, 
              and authentic hospitality come together. Created for travelers seeking relaxation, privacy, 
              and meaningful experiences, the property offers a refreshing escape from the noise of everyday life.
            </p>
            <p>
              Whether you are visiting for leisure, family time, or quiet reflection, Glow LAWNS provides 
              a setting designed to help you slow down and reconnect.
            </p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Our Story Section with Leaf Image */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Leaf Image */}
            <div className="relative flex justify-center lg:justify-end order-2 lg:order-1">
              <div className="relative">
                {/* Leaf image - displayed exactly as provided, no filters */}
                <img
                  src="/glowlawns-leaf-partners.png"
                  alt="Golden leaf with 4 veins representing the 4 partners of Glow Lawns"
                  className="relative w-64 md:w-80 h-auto object-contain"
                  data-testid="partners-leaf"
                />
                
                {/* Caption */}
                <p className="text-center mt-6 text-sm text-white/50 italic">
                  The 4 veins represent our 4 founding partners
                </p>
              </div>
            </div>
            
            {/* Story Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-[#C9A227] mb-2">
                <Heart className="w-5 h-5" />
                <span className="text-sm uppercase tracking-widest">Our Story</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-light text-white leading-tight">
                From Vision to <span className="text-[#C9A227]">Sanctuary</span>
              </h2>
              
              <div className="space-y-5 text-white/70 text-base leading-relaxed">
                <p>
                  Glow LAWNS began as a simple vision — to create a serene sanctuary where guests could 
                  experience the beauty of nature alongside the warmth of genuine Kerala hospitality.
                </p>
                <p>
                  What started as a heartfelt family dream gradually evolved into a tranquil getaway 
                  welcoming guests from near and far. Every corner of the property reflects this original 
                  intention: to offer comfort, calm, and a stay that feels personal rather than commercial.
                </p>
              </div>
            </div>
          </div>
          
          {/* Founders Section */}
          <div className="mt-20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 mb-8">
              <Users className="w-4 h-4 text-[#C9A227]" />
              <span className="text-xs uppercase tracking-widest text-[#C9A227]">The Founders</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="group p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl font-light text-[#C9A227]">PK</span>
                </div>
                <h3 className="text-white font-medium text-sm md:text-base">P K Nithin Raj</h3>
              </div>
              
              <div className="group p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl font-light text-[#C9A227]">AS</span>
                </div>
                <h3 className="text-white font-medium text-sm md:text-base">Akhil Sudhakar</h3>
              </div>
              
              <div className="group p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl font-light text-[#C9A227]">AR</span>
                </div>
                <h3 className="text-white font-medium text-sm md:text-base">Athul Raj</h3>
              </div>
              
              <div className="group p-6 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm hover:border-[#C9A227]/30 transition-all duration-300">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-2xl font-light text-[#C9A227]">AS</span>
                </div>
                <h3 className="text-white font-medium text-sm md:text-base">Akash Soman</h3>
              </div>
            </div>
            
            <p className="mt-8 text-white/50 text-sm italic max-w-xl mx-auto">
              Four friends, one vision — united by a shared dream to create a sanctuary where nature meets hospitality.
            </p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Location & Natural Setting */}
      <section className="py-20 md:py-28" style={{ backgroundImage: "radial-gradient(circle at 70% 20%, rgba(14,80,52,0.12), transparent 50%)" }}>
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[#C9A227] mb-4">
              <MapPin className="w-5 h-5" />
              <span className="text-sm uppercase tracking-widest">Location & Natural Setting</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Surrounded by <span className="text-[#C9A227]">Nature's Embrace</span>
            </h2>
          </div>
          
          <div className="space-y-6 text-white/70 text-base md:text-lg leading-relaxed text-center max-w-3xl mx-auto">
            <p>
              Surrounded by lush greenery, open skies, and refreshing coastal breezes, Glow LAWNS offers 
              a soothing natural environment that feels far removed from city stress while remaining 
              easily accessible.
            </p>
            <p className="text-white/60 italic">
              The atmosphere is gentle, quiet, and restorative — ideal for rest, reflection, 
              and unhurried moments.
            </p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* What Makes Us Unique */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-[#C9A227] mb-4">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm uppercase tracking-widest">What Makes Us Unique</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              Space, Simplicity, <span className="text-[#C9A227]">Serenity</span>
            </h2>
            
            <p className="text-white/60 text-base max-w-2xl mx-auto">
              Glow LAWNS is designed around space, simplicity, and serenity.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm space-y-4">
              <FeatureItem>Expansive green lawns and open spaces</FeatureItem>
              <FeatureItem>Peaceful natural surroundings</FeatureItem>
            </div>
            <div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm space-y-4">
              <FeatureItem>Personalized care and attentive service</FeatureItem>
              <FeatureItem>Ideal for family stays, retreats, and small gatherings</FeatureItem>
            </div>
          </div>
          
          <p className="text-center text-white/60 mt-10 text-base max-w-2xl mx-auto">
            The focus is not just accommodation, but the feeling of ease and comfort 
            that accompanies your stay.
          </p>
        </div>
      </section>

      <SectionDivider />

      {/* Stay & Experience */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[#C9A227] mb-4">
              <Home className="w-5 h-5" />
              <span className="text-sm uppercase tracking-widest">Stay & Experience</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
              Thoughtfully <span className="text-[#C9A227]">Designed</span>
            </h2>
            
            <div className="space-y-6 text-white/70 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              <p>
                Rooms at Glow LAWNS are thoughtfully designed to offer comfort and simplicity. 
                Clean lines, calming views, and modern amenities ensure guests can unwind effortlessly.
              </p>
              <p>
                The experience is intentionally balanced — blending convenience with a relaxed, 
                nature-centered ambiance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Hospitality Philosophy */}
      <section className="py-20 md:py-28" style={{ backgroundImage: "radial-gradient(circle at 30% 70%, rgba(201,162,39,0.08), transparent 45%)" }}>
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-[#C9A227] mb-4">
              <Heart className="w-5 h-5" />
              <span className="text-sm uppercase tracking-widest">Our Hospitality Philosophy</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
              Warmth, Care, <span className="text-[#C9A227]">Attention to Detail</span>
            </h2>
            
            <div className="space-y-6 text-white/70 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              <p>
                Hospitality at Glow LAWNS is rooted in warmth, care, and attention to detail. 
                Every guest is welcomed with sincerity and supported with a genuine desire to make 
                their stay restful and memorable.
              </p>
              <p className="text-white/60 italic">
                We believe meaningful hospitality is built on presence, not formality — creating 
                an environment where guests feel relaxed, valued, and completely at ease.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Invitation CTA */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 mb-8">
            <Leaf className="w-4 h-4 text-emerald-400" />
            <span className="text-xs uppercase tracking-widest text-emerald-400">An Invitation</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-light text-white mb-8 leading-tight">
            Experience <span className="text-[#C9A227]">Glow LAWNS</span>
          </h2>
          
          <p className="text-white/70 text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Whether visiting for rest, celebration, or a quiet nature escape, Glow LAWNS Resort & Homestay 
            invites you to experience comfort, serenity, and heartfelt hospitality.
          </p>
          
          {/* Arrival/Departure Message */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12 text-lg">
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-emerald-400" />
              <span className="text-white/80">Arrive with expectations.</span>
            </div>
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#C9A227]" />
              <span className="text-white/80">Leave with memories.</span>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PremiumButton 
              href="/#stay" 
              variant="primary" 
              className="text-base px-8 py-4"
              data-testid="about-explore-stays"
            >
              <span className="inline-flex items-center gap-2">
                Explore Our Stays
                <ArrowRight className="w-4 h-4" />
              </span>
            </PremiumButton>
            <PremiumButton 
              href="/contact" 
              variant="secondary" 
              className="text-base px-8 py-4"
              data-testid="about-contact-us"
            >
              Contact Us
            </PremiumButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
